import { useState, useEffect, useRef, useCallback } from 'react';

export type ConnectionState = 'online' | 'offline' | 'reconnecting';

interface ConnectionStatus {
  /** Current connection state */
  state: ConnectionState;
  /** True after a successful reconnect (resets when consumed) */
  wasOffline: boolean;
  /** Clear the wasOffline flag after consuming it */
  clearWasOffline: () => void;
}

/**
 * Detects connection state using navigator.onLine, visibilitychange, and /health check.
 *
 * - 'offline': browser reports offline
 * - 'reconnecting': browser says online but verifying with /health
 * - 'online': confirmed server is reachable
 */
export function useConnectionStatus(): ConnectionStatus {
  const [state, setState] = useState<ConnectionState>(navigator.onLine ? 'online' : 'offline');
  const [wasOffline, setWasOffline] = useState(false);
  const wasOfflineRef = useRef(false);
  const healthCheckRef = useRef(false); // prevent concurrent checks

  const clearWasOffline = useCallback(() => setWasOffline(false), []);

  const checkHealth = useCallback(async () => {
    if (healthCheckRef.current) return; // already checking
    healthCheckRef.current = true;
    setState('reconnecting');
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      const res = await fetch('/health', {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (res.ok) {
        setState('online');
        if (wasOfflineRef.current) {
          setWasOffline(true);
          wasOfflineRef.current = false;
        }
      } else {
        setState('offline');
        wasOfflineRef.current = true;
      }
    } catch {
      setState('offline');
      wasOfflineRef.current = true;
    } finally {
      healthCheckRef.current = false;
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      wasOfflineRef.current = true;
      checkHealth();
    };

    const handleOffline = () => {
      setState('offline');
      wasOfflineRef.current = true;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        checkHealth();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkHealth]);

  return { state, wasOffline, clearWasOffline };
}
